import searchControlQueryDataValidation from '@/helpers/searchControlQueryDataValidation';
import searchFilterQueryDataValidation from '@/helpers/searchFilterQueryDataValidation';
import {
  searchFilterDefaults,
  searchPaginationDefaults,
  type SearchControlData,
  type SearchFilterData,
  type SearchPaginationData,
} from '@/models/insertionsParameters';
import { InsertionStatus, type FiltersWithCount, type Insertion } from '@/types';

export type InsertionProcessingGoal = 'publish' | 'archive' | 'reactivate';

export const insertionDisplayOrder: InsertionStatus[] = [
  InsertionStatus.PendingApproval,
  InsertionStatus.PendingRepublishApproval,
  InsertionStatus.Approved,
  InsertionStatus.Draft,
  InsertionStatus.Published,
  InsertionStatus.Republishing,
  InsertionStatus.Deleted,
];

export enum ApiCallStatus {
  READY = 'READY',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export interface InsertionsListSearch {
  nextPageToFetch: string | undefined;
  nextInsertionStatusToFetch: InsertionStatus;
  insertionStatusesToFetch: InsertionStatus[];
  objectReference: string | undefined;
  username: string | undefined;
  postalCode: string | undefined;
}

export interface InsertionsListState {
  insertions: Insertion[];
  apiCallStatus: ApiCallStatus;
  apiCallInsertionId?: string;
  insertionActionSuccessTitle?: string;
  insertionActionSuccessMessage?: string;
  archivedInsertionId?: string;
  deletedInsertionId?: string;
  search: InsertionsListSearch;
  processingInsertionIds: string[];
  processingInsertionGoals: Record<string, InsertionProcessingGoal>;
}

export function findInsertionById(insertions: Insertion[], id: string): Insertion {
  return insertions.find((insertion) => insertion.id === id) as Insertion;
}

export function getMessageForStatus(status: InsertionStatus): string | undefined {
  if (status === InsertionStatus.Approved) {
    return 'manageAdsPage.toasts.insertionSuccessfullyApproved';
  }
  if (status === InsertionStatus.Archived) {
    return 'manageAdsPage.toasts.insertionSuccessfullyArchived';
  }
  return undefined;
}

export function getInitialInsertionsListState(): InsertionsListState {
  return {
    insertions: [],
    apiCallStatus: ApiCallStatus.READY,
    apiCallInsertionId: undefined,
    insertionActionSuccessTitle: undefined,
    insertionActionSuccessMessage: undefined,
    archivedInsertionId: undefined,
    deletedInsertionId: undefined,
    search: {
      nextPageToFetch: undefined,
      nextInsertionStatusToFetch: InsertionStatus.PendingApproval,
      insertionStatusesToFetch: insertionDisplayOrder,
      objectReference: undefined,
      username: undefined,
      postalCode: undefined,
    },
    processingInsertionIds: [],
    processingInsertionGoals: {},
  };
}

export interface InsertionsSearchState {
  insertionsChanged: boolean;
  searchFilter: SearchFilterData;
  searchFilterLatest: SearchFilterData;
  searchControl: SearchControlData;
  searchPagination: SearchPaginationData;
  filtersWithCount: FiltersWithCount;
}

export function getInitialInsertionsSearchState(
  searchFilters?: SearchFilterData,
  searchControl?: SearchControlData | Record<string, unknown>,
): InsertionsSearchState {
  const validatedSearchFilter = searchFilterQueryDataValidation(
    searchFilters || searchFilterDefaults,
  );

  return {
    insertionsChanged: false,
    searchFilter: { ...validatedSearchFilter },
    searchFilterLatest: { ...validatedSearchFilter },
    searchControl: {
      ...searchControlQueryDataValidation(searchControl),
    },
    searchPagination: { ...searchPaginationDefaults },
    filtersWithCount: { total: 0, filters: [] },
  };
}
